/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable react/jsx-max-depth */
import { formatMoney } from '@transferwise/formatting';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { InView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';

import ConditionalWrapper from '../resources/ConditionalWrapper';
import { trackTableViewed, getTrackingTriggerIndex } from '../resources/tracking';
import { Table } from '../resources/tracking/types';

import style from './ProviderComparisonTable.module.less';
import RouteRow from './components/RouteRow';

function createClassNames(classNames) {
  return classNames.map((className) => `${style[className]}`).join(' ');
}

const SHOW_DELIVERY_ESTIMATES = false;

const ProviderComparisonTable = ({
  name,
  alias,
  logo,
  sendAmount,
  sourceCurrency,
  routes,
  trackEvent,
  onCallToActionClick,
}) => {
  const { locale, formatMessage } = useIntl();
  const [centreRouteViewed, setCentreRouteViewed] = useState(false);

  const centreRouteIndex = routes ? getTrackingTriggerIndex(routes.length) : null;

  const onTableView = (inView) => {
    if (!inView) {
      return;
    }

    setCentreRouteViewed(true);

    const trackingContext = {
      provider: {
        name,
      },
      amount: sendAmount,
      source: sourceCurrency,
      componentType: Table.ProviderComparison,
      routes,
    };

    trackTableViewed(trackingContext, trackEvent);
  };

  return (
    <div className={style['card-provider-table']}>
      <table className={style['table-plain']}>
        <caption className={style['table-plain__caption']}>
          <FormattedMessage values={{ name }} id="table-caption" />
        </caption>
        <thead className={createClassNames(['thead'])}>
          <tr className={style['table-plain__row']}>
            <th className={style['table-plain__th']}>
              <FormattedMessage id="currency" />
            </th>
            <th className={createClassNames(['table-plain__th', 'only-mobile'])}>
              <FormattedMessage id="provider" />
            </th>
            {SHOW_DELIVERY_ESTIMATES && (
              <th className={style['table-plain__th']}>
                <FormattedMessage id="should-arrive" />
              </th>
            )}
            <th className={style['table-plain__th']}>
              <FormattedMessage id="transfer-fee" />
            </th>
            <th className={style['table-plain__th']}>
              <FormattedMessage id="exchange-rate" />
            </th>
            <th className={style['table-plain__th']}>
              <FormattedMessage id="you-receive" />
              <br />
              <span className={style['table-plain__th--small']} data-testid="sending-amount">
                <FormattedMessage
                  values={{
                    amountWithCurrency: formatMoney(sendAmount, sourceCurrency, locale),
                  }}
                  id="sending-amount"
                />
              </span>
            </th>
            <th
              className={createClassNames([
                'table-plain__th',
                'featured--th',
                'send-with-tw-heading',
              ])}
            >
              <span className={style['send-with-tw-heading__item']}>
                <FormattedMessage
                  values={{
                    logo: (
                      <img
                        className={style['send-with-tw-heading__logo']}
                        src="https://wise.com/public-resources/assets/logos/wise-personal/logo_inverse.svg"
                        alt={formatMessage({ id: 'wise-logo-alt-text' })}
                      />
                    ),
                  }}
                  id="sending-with-wise"
                />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {routes.map(({ baseProviderQuote, comparedProviderQuote }, index) => {
            const isCentreRoute = index === centreRouteIndex;
            return (
              // eslint-disable-next-line react/jsx-key
              <ConditionalWrapper
                condition={!centreRouteViewed && isCentreRoute}
                wrapper={(children) => (
                  <InView onChange={(inView, _) => onTableView(inView)} threshold={1} triggerOnce>
                    {({ _, ref }) =>
                      React.cloneElement(children, {
                        ref: ref,
                        isCentre: true,
                      })
                    }
                  </InView>
                )}
                key={comparedProviderQuote.targetCurrency}
              >
                <RouteRow
                  key={baseProviderQuote.targetCurrency}
                  name={name}
                  alias={alias}
                  logo={logo}
                  baseProviderQuote={baseProviderQuote}
                  comparedProviderQuote={comparedProviderQuote}
                  locale={locale}
                  showDeliveryEstimates={SHOW_DELIVERY_ESTIMATES}
                  sendAmount={sendAmount}
                  onCallToActionClick={onCallToActionClick}
                />
              </ConditionalWrapper>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

ProviderComparisonTable.propTypes = {
  name: PropTypes.string.isRequired,
  alias: PropTypes.string.isRequired,
  logo: PropTypes.string,
  sendAmount: PropTypes.number.isRequired,
  sourceCurrency: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.shape({})),
  trackEvent: PropTypes.func,
  onCallToActionClick: PropTypes.func,
};

ProviderComparisonTable.defaultProps = {
  logo: '',
  routes: [],
  trackEvent: () => {},
  onCallToActionClick: () => {},
};

export default ProviderComparisonTable;
